<template>
  <div class="cell-input-wrapper" :class="{ changed: isChanged }">
    <span v-if="!isEditable" class="readonly-text">{{ displayValue }}</span>
    <div v-else class="editor">
      <div class="text-input-wrapper" :class="{ error: errorMessage }">
        <div v-if="errorMessage" class="tooltip-container">
          <tooltip-validation-alert :errorMessage="errorMessage" />
        </div>
        <input
          class="text-input"
          type="text"
          :value="inputValue"
          @input="handleInput"
          @focus="handleFocus"
          @blur="handleBlur"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TooltipValidationAlert from "../../components/tooltips/TooltipValidationAlert.vue";

export default {
  name: "CellRendererPurchasedHolidayDaysInput",
  components: {
    TooltipValidationAlert,
  },
  data() {
    return {
      inputValue:
        this.params &&
        this.params.value !== undefined &&
        this.params.value !== null
          ? this.params.value
          : "",
      errorMessage: "",
      valueOnFocus: null,
    };
  },
  computed: {
    isEditable() {
      const parent =
        this.params &&
        this.params.context &&
        this.params.context.componentParent
          ? this.params.context.componentParent
          : null;
      const data = this.params && this.params.data ? this.params.data : {};
      const isClosed = data.isClosed || false;
      return parent && parent.isEdit && !isClosed;
    },
    displayValue() {
      const value = this.params ? this.params.value : null;
      if (
        value === 0 ||
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return "-";
      }
      return `${value}`;
    },
    remainingDays() {
      const data = this.params && this.params.data ? this.params.data : {};
      return Number(data.remainingDays) || 0;
    },
    isChanged() {
      if (!this.isEditable) return false;
      const parent =
        this.params &&
        this.params.context &&
        this.params.context.componentParent
          ? this.params.context.componentParent
          : null;
      if (!parent || !parent.originalTableRows || !this.params.data) {
        return false;
      }
      const currentRow = this.params.data;
      const field =
        this.params && this.params.colDef ? this.params.colDef.field : null;
      if (!field) {
        return false;
      }
      const originalRow = parent.originalTableRows.find(
        row => row.id === currentRow.id
      );
      if (!originalRow) return false;
      const currentValue = Number(this.inputValue);
      const originalValue = originalRow[field];
      return currentValue !== originalValue;
    },
  },
  methods: {
    refresh(params) {
      this.params = params;
      const value =
        params.value === null || params.value === undefined ? "" : params.value;
      this.inputValue = this.isEditable ? value : "";
      if (!this.isEditable) {
        this.errorMessage = "";
        this.reportValidation(true);
      }
      return true;
    },
    handleInput(event) {
      const rawValue = event.target.value;
      this.inputValue = rawValue;
      if (rawValue === "") {
        this.setError("値を入力してください");
        this.reportValidation(false, "値を入力してください");
        return;
      }
      const numericValue = Number(rawValue);
      if (Number.isNaN(numericValue)) {
        this.setError("数値を入力してください");
        this.reportValidation(false, "数値を入力してください");
        return;
      }
      const validation = this.validate(numericValue);
      if (!validation.isValid) {
        this.setError(validation.message);
        this.reportValidation(false, validation.message);
        return;
      }
      this.clearError();
      const field =
        this.params && this.params.colDef ? this.params.colDef.field : null;
      if (!field) {
        return;
      }
      this.params.node.setDataValue(field, numericValue);
      this.reportValidation(true);
    },

    /** フォーカス時の入力値を保持 */
    handleFocus() {
      this.valueOnFocus = String(this.inputValue).trim();
    },

    /** フォーカスを外した時の処理 */
    handleBlur() {
      const rawValue = String(this.inputValue).trim();
      // フォーカス時の入力値と同じ場合は処理しない
      if (rawValue === this.valueOnFocus) {
        return;
      }

      // バリデーションエラー時は0に設定
      if (rawValue === "") {
        this.forceCarryoverToZero();
        return;
      }
      const numericValue = Number(rawValue);
      if (Number.isNaN(numericValue)) {
        this.forceCarryoverToZero();
        return;
      }
      const validation = this.validate(numericValue);
      if (!validation.isValid) {
        this.forceCarryoverToZero();
        return;
      }

      this.applyAutoCarryover(numericValue);
    },
    validate(value) {
      if (value < 0) {
        return { isValid: false, message: "0以上の値を入力してください" };
      }
      if (value > this.remainingDays) {
        return {
          isValid: false,
          message: `残日数${this.remainingDays}日以下で入力してください`,
        };
      }
      return { isValid: true };
    },
    applyAutoCarryover(value) {
      const carryoverValue = Math.max(this.remainingDays - value, 0);
      this.params.node.setDataValue("rolloverHolidayDays", carryoverValue);
      this.params.api.refreshCells({
        rowNodes: [this.params.node],
        columns: ["rolloverHolidayDays"],
        force: true,
      });
    },

    /** 繰越しを0に設定 */
    forceCarryoverToZero() {
      this.params.node.setDataValue("rolloverHolidayDays", 0);
      this.params.api.refreshCells({
        rowNodes: [this.params.node],
        columns: ["rolloverHolidayDays"],
        force: true,
      });
    },

    setError(message) {
      this.errorMessage = message;
    },
    clearError() {
      this.errorMessage = "";
    },
    reportValidation(isValid, message = "") {
      const parent =
        this.params &&
        this.params.context &&
        this.params.context.componentParent
          ? this.params.context.componentParent
          : null;
      if (!parent || typeof parent.handleCellValidation !== "function") {
        return;
      }
      const rowData = this.params && this.params.data ? this.params.data : {};
      const rowId =
        rowData.id ||
        (this.params && this.params.node ? this.params.node.id : null);
      parent.handleCellValidation(rowId, this.params.colDef.field, {
        isValid,
        message,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.cell-input-wrapper {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: 0;
  margin: 0;

  &.changed {
    .text-input-wrapper {
      background-color: #fff3cd;
    }
  }

  .readonly-text {
    display: block;
    width: 100%;
    height: 100%;
    padding: 8px;
    box-sizing: border-box;
  }

  .editor {
    width: 100%;
    height: 100%;
    box-sizing: border-box;
  }

  .text-input-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 100%;
    min-height: 52px;
    padding: 8px;
    margin: 0;
    border: 1px solid $aisea-mouse-gray;
    border-radius: 6px;
    box-sizing: border-box;
    transition: border-color 0.3s, box-shadow 0.3s;

    &:focus-within {
      border-color: $aisea-blue;
      box-shadow: 0 0 0 2px rgba(45, 156, 219, 0.1);
    }

    &.error {
      border-color: $aisea-red;
      background-color: rgba($aisea-red, 0.05);
    }

    .text-input {
      width: 100%;
      border: none !important;
      outline: none !important;
      background: transparent;
      text-align: right;
      font-size: 14px;
      padding: 0;
      margin: 0;
      flex: 1;
      min-width: 0;
    }

    .tooltip-container {
      display: flex;
      position: relative;
      margin-right: 8px;
      flex-shrink: 0;
    }
  }
}
</style>
